import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { bool, func, number, object, string } from 'prop-types';
import { Button } from '@landtrustinc/design-system';

import InquiryAuthenticationPage from '../../InquiryAuthenticationPage/InquiryAuthenticationPage';
import InquiryEmailGate from '../InquiryEmailGate';
import InquiryFieldError from '../InquiryFieldError';
import InquiryWizardPanel from '../InquiryWizardPanel';
import InquiryWizardSection from '../InquiryWizardSection';
import { canSendInquiry, hasSavedInquiryDraft, isInquiryEmailUnverified } from '../inquiryAccess';

import css from './InquiryWizardStepSignup.module.css';

const landownerFirstName = listing => {
  const displayName =
    listing?.author?.attributes?.profile?.displayName ||
    listing?.listing?.author?.attributes?.profile?.displayName;
  return displayName ? displayName.split(' ')[0] : '';
};

const InquirySignup = ({ listing, inquirySubmitInFlight, inquirySubmitAttempt }) => {
  const firstName = landownerFirstName(listing);

  return (
    <InquiryWizardSection>
      <h1 className={css.title}>
        <FormattedMessage id="InquiryWizard.desktopSignupTitle" values={{ firstName }} />
      </h1>
      <div>
        <InquiryAuthenticationPage
          fromLTPlusPage
          useRedirects={false}
          inquiryButton
          plusLoginButton={false}
          listing={listing}
          inquirySubmitInFlight={inquirySubmitInFlight}
          inquirySubmitAttempt={inquirySubmitAttempt}
        />
      </div>
    </InquiryWizardSection>
  );
};

const InquiryWizardStepSignup = ({
  listing,
  formValues,
  currentUser,
  errorMessage,
  inquirySubmitInFlight,
  inquirySubmitAttempt,
  onSubmitInquiry,
}) => {
  if (isInquiryEmailUnverified(currentUser)) {
    return (
      <InquiryWizardPanel>
        <InquiryEmailGate
          currentUser={currentUser}
          saved={hasSavedInquiryDraft(formValues)}
          landownerName={landownerFirstName(listing)}
        />
      </InquiryWizardPanel>
    );
  }

  const showRetry = canSendInquiry(currentUser) && errorMessage && !inquirySubmitInFlight;

  return (
    <InquiryWizardPanel>
      {errorMessage ? (
        <InquiryFieldError className={css.error} data-testid="inquiry-submit-error">
          {errorMessage}
        </InquiryFieldError>
      ) : null}
      {showRetry ? (
        <div className={css.retry}>
          <Button
            variant="primary"
            type="button"
            fullWidth
            data-testid="send-inquiry-button"
            disabled={inquirySubmitInFlight}
            isLoading={inquirySubmitInFlight}
            onClick={() => onSubmitInquiry(formValues)}
          >
            <FormattedMessage id="InquiryWizard.sendInquiry" />
          </Button>
        </div>
      ) : null}
      <InquirySignup
        listing={listing}
        inquirySubmitInFlight={inquirySubmitInFlight}
        inquirySubmitAttempt={inquirySubmitAttempt}
      />
    </InquiryWizardPanel>
  );
};

InquirySignup.defaultProps = {
  inquirySubmitInFlight: false,
  inquirySubmitAttempt: 0,
};

InquirySignup.propTypes = {
  listing: object.isRequired,
  inquirySubmitInFlight: bool,
  inquirySubmitAttempt: number,
};

InquiryWizardStepSignup.defaultProps = {
  formValues: null,
  currentUser: null,
  errorMessage: null,
  inquirySubmitInFlight: false,
  inquirySubmitAttempt: 0,
  onSubmitInquiry: null,
};

InquiryWizardStepSignup.propTypes = {
  listing: object.isRequired,
  formValues: object,
  currentUser: object,
  errorMessage: string,
  inquirySubmitInFlight: bool,
  inquirySubmitAttempt: number,
  onSubmitInquiry: func,
};

export default injectIntl(InquiryWizardStepSignup);
